// ============================================================================
// The questions the app asks before a bid goes to pricing (design reference
// screen 3, "Before this goes to pricing, the app asks"). They are the ones the
// September prep sheets already asked; answering them here means the number on
// the next screen is right the first time.
// ============================================================================

export interface FollowUp {
  key: string;
  question: string;
}

export interface BuildingForQuestions {
  facility_type: string;
  num_or_suites: number | null;
  total_sqft: number | null;
  sterile_sqft: number | null;
}

/** The sterile shares the book records for buildings with the same OR count. */
export interface PeerShareBand {
  or_band: string;
  low: number;
  high: number;
  n: number;
}

// The prompt's stated range for square feet per OR on a surgery center.
const SF_PER_OR_LOW = 2000;
const SF_PER_OR_HIGH = 4000;

export function followUps(b: BuildingForQuestions, band: PeerShareBand | null): FollowUp[] {
  if (b.facility_type === 'Dialysis') {
    return [
      { key: 'home_or_iso', question: 'Are there isolation or home-dialysis training rooms, and are they in our scope?' },
      { key: 'block_days', question: 'Which days do they run treatments — any days with no patients?' },
    ];
  }
  const out: FollowUp[] = [];
  const ors = b.num_or_suites ?? 0;
  if (band && b.total_sqft && b.sterile_sqft != null && b.total_sqft > 0) {
    const share = b.sterile_sqft / b.total_sqft;
    if (share < band.low || share > band.high) {
      out.push({
        key: 'sterile_share',
        question: `Sterile share is ${Math.round(share * 100)}%. Every ${band.or_band} center in the book runs ${Math.round(band.low * 100)}–${Math.round(band.high * 100)}% (${band.n} buildings). Is any PACU or pre-op space counted as sterile, or left out?`,
      });
    }
  }
  if (ors > 0 && b.total_sqft) {
    const perOr = b.total_sqft / ors;
    if (perOr < SF_PER_OR_LOW || perOr > SF_PER_OR_HIGH) {
      out.push({
        key: 'sf_per_or',
        question: `That is ${Math.round(perOr).toLocaleString('en-US')} sf of building per OR — outside the usual 2,000–4,000. Are the OR count and the square footage both right?`,
      });
    }
  }
  out.push({ key: 'clinic_wing', question: 'Is there a clinic wing, and is it in our scope? (How many sqft?)' });
  out.push({ key: 'or_turnover', question: 'Who does OR turnover between cases — them or us?' });
  out.push({ key: 'block_days', question: 'Any block days with no cases?' });
  return out;
}

/** Answered questions become dated lines in the walkthrough notes. */
export function notesWithAnswers(
  existing: string | null,
  asked: { question: string; answer: string }[],
  today: string,
): string {
  const lines = asked.map((a) => `${today} · Asked: ${a.question}${a.answer.trim() ? ` — ${a.answer.trim()}` : ''}`);
  if (!lines.length) return existing ?? '';
  return [existing?.trim(), ...lines].filter(Boolean).join('\n');
}
